/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Component, Injector, Input, OnInit } from '@angular/core';
import { LineIsolationService } from './line-isolation.service';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import { LineIsolationListComponent } from './line-isolation-list.component';
import { Observable } from 'rxjs';
import { DashGisOverlay } from '../../core/ui/overlay/dash-gis-overlay.service';
import { LineIsolationListOverlayRef } from './line-isolation-list-overlay-ref';
import { mergeMap, take } from 'rxjs/operators';
import { LoggerService } from '../../modules/shared/services/logger.service';

@Component({
    selector: 'dash-gis-line-isolation',
    template: ` <button
        mat-icon-button
        matTooltip="Line Isolation Query"
        (click)="query()"
        *ngIf="isLineIsolationEnabled$ | async"
    >
        <mat-icon svgIcon="isolation" aria-label="Line Isolation Query"></mat-icon>
    </button>`,
})
export class LineIsolationComponent implements OnInit {
    @Input() public layerName: string;
    @Input() public mapName: string;
    @Input() public node: string;

    isLineIsolationEnabled$: Observable<boolean>;

    constructor(
        private logger: LoggerService,
        private lineIsolationService: LineIsolationService,
        private overlay: DashGisOverlay,
        private injector: Injector
    ) {}

    public ngOnInit() {
        this.isLineIsolationEnabled$ = this.lineIsolationService.isIsolationEnabled(this.mapName, this.layerName);
    }

    public query() {
        this.lineIsolationService
            .getSelectedFeature(this.mapName, this.layerName)
            .pipe(
                take(1),
                mergeMap((feature) => {
                    if (feature) {
                        this.lineIsolationService.query(this.mapName, this.layerName, feature);
                        const overlayRef = this.overlay.create({
                            hasBackdrop: false,
                            panelClass: 'dash-gis-floating-panel',
                            width: '350px',
                            positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
                        });
                        const lineIsolationOverlayRef = new LineIsolationListOverlayRef(overlayRef);
                        const injector = this.createInjector(lineIsolationOverlayRef);
                        const listPortal = new ComponentPortal(LineIsolationListComponent, null, injector);
                        overlayRef.attach(listPortal);
                        return overlayRef.detachments();
                    }
                })
            )
            .subscribe(() => {
                this.lineIsolationService.clearIsolationData();
            });
    }

    private createInjector(overlayRef: LineIsolationListOverlayRef): PortalInjector {
        const tokens = new WeakMap();
        tokens.set(LineIsolationListOverlayRef, overlayRef);
        return new PortalInjector(this.injector, tokens);
    }
}
